/**
 * Frozen copy of the tuck-under-car drive-in. The baseline clips under the
 * body with no halo. Keep this file as the saved asset — iterate on
 * LapturaDriveIn.jsx instead.
 */
import { animate, motion, useMotionTemplate, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import React, { useEffect, useId } from 'react';
import { cn } from '../../lib/utils';
import parts from '../../assets/brand/laptura-wordmark-parts.json';

export const DRIVE_HOLD_SECONDS = 0.5;
export const DRIVE_TRAVEL_SECONDS = 3;
export const DRIVE_SECONDS = DRIVE_HOLD_SECONDS + DRIVE_TRAVEL_SECONDS;

const [boxX, boxY, , boxH] = parts.viewBox.split(' ').map(Number);
export const START_CAR_X = 18;
export const END_CAR_X = parts.car.x;
export const TRAVEL = END_CAR_X - START_CAR_X;
export const START_OFFSET = START_CAR_X - END_CAR_X;
const NOSE_PAD = 2;
export const RULE_TUCK = 32;
export const RULE_WIDTH = 379.222;

export function viewBoxWidthAt(p) {
  return START_CAR_X + p * TRAVEL + parts.car.width + NOSE_PAD - boxX;
}

export function ruleClipRightAt(p) {
  return START_CAR_X + p * TRAVEL + RULE_TUCK;
}

export function ruleClipFracAt(p) {
  return ruleClipRightAt(p) / RULE_WIDTH;
}

export function letterAppearAt(letterX) {
  return Math.max(0, Math.min(1, (letterX - START_CAR_X) / TRAVEL));
}

function Letter({ d, appearAt, progress, still }) {
  const opacity = useTransform(progress, [appearAt, Math.min(1, appearAt + 0.12)], [0, 1]);
  if (still) return <path d={d} />;
  return <motion.path d={d} style={{ opacity }} />;
}

export function LapturaDriveInTuck({ className, progress: progressOverride }) {
  const still = useReducedMotion();
  const clipId = useId().replace(/:/g, '');
  const frozen = still || progressOverride != null;
  const progress = useMotionValue(frozen ? (progressOverride ?? 1) : 0);
  const carX = useTransform(progress, [0, 1], [START_OFFSET, 0]);
  const vbW = useTransform(progress, viewBoxWidthAt);
  const viewBox = useMotionTemplate`${boxX} ${boxY} ${vbW} ${boxH}`;
  const clipFrac = useTransform(progress, ruleClipFracAt);
  const clipD = useMotionTemplate`M0 0H${clipFrac}V1H0Z`;

  useEffect(() => {
    if (progressOverride != null) {
      progress.set(progressOverride);
      return undefined;
    }
    if (still) return undefined;
    let travel;
    const hold = animate(progress, 0, { duration: DRIVE_HOLD_SECONDS });
    hold.then(() => {
      travel = animate(progress, 1, {
        duration: DRIVE_TRAVEL_SECONDS,
        ease: [0.48, 0.0, 0.16, 1],
      });
    });
    return () => {
      hold.stop();
      travel?.stop();
    };
  }, [progress, still, progressOverride]);

  return (
    <motion.svg
      viewBox={viewBox}
      className={cn('overflow-hidden', className)}
      fill="currentColor"
      role="img"
      aria-label="Laptura"
    >
      <defs>
        <clipPath id={clipId} clipPathUnits="objectBoundingBox">
          <motion.path d={clipD} />
        </clipPath>
      </defs>
      <path d={parts.rule} clipPath={`url(#${clipId})`} />
      {parts.letters.map((letter) => (
        <Letter
          key={letter.x}
          d={letter.d}
          appearAt={letterAppearAt(letter.x)}
          progress={progress}
          still={still}
        />
      ))}
      <motion.path d={parts.car.d} style={{ x: still ? 0 : carX }} />
    </motion.svg>
  );
}
